// src/services/calendarService.js

// Import Firebase Firestore functions for database operations
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";

/**
 * Calendar Service
 *
 * Comprehensive service for managing calendar events in Firebase Firestore.
 * Handles all CRUD operations for job-related appointments and events.
 *
 * Features:
 * ✅ Complete CRUD operations (Create, Read, Update, Delete)
 * ✅ Error handling with meaningful messages
 * ✅ Input validation for data integrity
 * ✅ Query filtering and sorting capabilities
 * ✅ Comprehensive logging for debugging
 * ✅ Consistent return patterns for better UX
 * ✅ JSDoc documentation for developer experience
 *
 * Collection Structure:
 * - Collection: 'calendar'
 * - Document fields: title, description, start, end, eventType, jobId, customerId
 */

// ==========================================
// COLLECTION REFERENCE
// ==========================================

// Firebase collection reference for calendar events
const CALENDAR_COLLECTION = "calendar";

/**
 * Get reference to the calendar collection
 *
 * @returns {CollectionReference} Firestore collection reference
 */
const getCalendarCollection = () => {
  return collection(db, CALENDAR_COLLECTION);
};

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Validate calendar event data before operations
 *
 * Ensures all required fields are present and properly formatted.
 * Provides clear error messages for missing or invalid data.
 *
 * @param {Object} calendar - Calendar event object to validate
 * @param {boolean} isUpdate - Whether this is for an update operation (allows missing fields)
 * @returns {Object} Validation result with isValid boolean and error message
 */
const validateCalendarData = (calendar, isUpdate = false) => {
  // Required fields for calendar events
  const requiredFields = ["title", "start", "end"];

  // Optional but recommended fields
  const optionalFields = ["description", "eventType", "jobId", "customerId"];

  // Check for required fields (only for create operations)
  if (!isUpdate) {
    for (const field of requiredFields) {
      if (!calendar[field]) {
        return {
          isValid: false,
          error: `Missing required field: ${field}`,
        };
      }
    }
  }

  // Validate title length if provided
  if (calendar.title && calendar.title.length > 200) {
    return {
      isValid: false,
      error: "Title must be 200 characters or less",
    };
  }

  // Validate event type if provided
  // Updated to include the new event types
  const validEventTypes = [
    "measurement",
    "installation",
    "price",
    "follow-up",
    "quote",
    "reschedule",
    "pickup",
    "delivery",
    "payment",
    "repair",
  ];

  if (calendar.eventType && !validEventTypes.includes(calendar.eventType)) {
    return {
      isValid: false,
      error: `Invalid event type. Must be one of: ${validEventTypes.join(
        ", "
      )}`,
    };
  }

  // Validate date fields if provided
  if (calendar.start && calendar.end) {
    const startDate = new Date(calendar.start);
    const endDate = new Date(calendar.end);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return {
        isValid: false,
        error: "Invalid date format for start or end date",
      };
    }

    if (startDate >= endDate) {
      return {
        isValid: false,
        error: "End date must be after start date",
      };
    }
  }

  return {
    isValid: true,
    error: null,
  };
};

/**
 * Sanitize calendar data before saving to database
 *
 * Cleans and formats data to ensure consistency in the database.
 * Removes any undefined values and trims string fields.
 *
 * @param {Object} calendar - Raw calendar data
 * @returns {Object} Sanitized calendar data
 */
const sanitizeCalendarData = (calendar) => {
  const sanitized = {};

  // List of valid fields to include in the document.
  //
  // ⚠️ ALLOWLIST GOTCHA — any field not in this list is silently stripped
  // before writeback. When adding a new schema field to calendar documents,
  // register it here FIRST. See `_framework/decisions.md` 2026-05-13 entry
  // "Calendar manual-sort pattern + sanitizer-allowlist lesson".
  //
  // sortOrder        — per-day manual reorder (2026-05-13)
  // location         — free-text address row (2026-05-13)
  // driverId         — Firebase Auth UID of the assigned driver. null/missing
  //                    = unassigned (2026-05-13)
  // driverSortOrder  — separate per-driver queue order; doesn't collide with
  //                    `sortOrder` (the per-day calendar order) (2026-05-13)
  // completedAt      — Firestore Timestamp when the driver hit "Mark as
  //                    done". null = pending (2026-05-13)
  // customerName     — denormalised snapshot of customer.name at event
  //                    create/update time. Stale if the customer is later
  //                    renamed (acceptable trade-off — see decisions log).
  //                    Avoids opening Firestore Rules to drivers reading the
  //                    customer collection. (2026-05-13)
  // customerPhone    — denormalised snapshot of customer.phone, same
  //                    rationale as customerName (2026-05-13)
  const validFields = [
    "title",
    "description",
    "start",
    "end",
    "eventType",
    "jobId",
    "customerId",
    "status",
    "notes",
    "location",
    "sortOrder",
    "driverId",
    "driverSortOrder",
    "completedAt",
    "customerName",
    "customerPhone",
  ];

  // Process each valid field
  validFields.forEach((field) => {
    if (calendar[field] !== undefined && calendar[field] !== null) {
      // Trim strings and remove extra whitespace
      if (typeof calendar[field] === "string") {
        sanitized[field] = calendar[field].trim();
      } else {
        sanitized[field] = calendar[field];
      }
    }
  });

  // Add metadata fields
  sanitized.updatedAt = Timestamp.now();

  return sanitized;
};

// ==========================================
// DENORMALISATION HELPERS
//
// These exist so drivers can see customer name/phone on their assigned
// events and so Firestore Rules can decide whether a driver is allowed to
// touch a job (via `jobs/{id}.assignedDriverIds`) without doing a list()
// query in the rules (which Firestore Rules don't support).
//
// The trade-off is: when a customer is renamed / has their phone changed,
// existing calendar events keep the OLD value until the event is touched.
// Documented under decisions.md 2026-05-13.
// ==========================================

/**
 * Look up the customer for an event and return a `{customerName,
 * customerPhone}` slice to merge onto the event before sanitisation.
 *
 * Returns `{}` when the event has no customerId, or when the lookup fails
 * for any reason — we never want a customer-read failure to block the
 * calendar write, so we swallow + log and let the event save without the
 * denormalised fields. The admin can re-save to retry.
 *
 * Reads `customer/{id}` (singular — see customerService for the
 * collection name).
 */
const _denormaliseCustomerFields = async (eventData) => {
  if (!eventData.customerId) return {};
  try {
    const ref = doc(db, "customer", eventData.customerId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return {};
    const customer = snap.data();
    return {
      customerName: customer.name || "",
      customerPhone: customer.phone || "",
    };
  } catch (error) {
    console.warn(
      "Denormalisation of customer fields failed; saving event without them.",
      error
    );
    return {};
  }
};

/**
 * Recompute `jobs/{jobId}.assignedDriverIds` from the current calendar
 * events on that job. Idempotent — safe to call any time, multiple times.
 *
 * The Firestore Rules use this array to decide which drivers can read /
 * write room data on a job. Whenever an event's `driverId` or `jobId`
 * changes, OR an event is deleted, the impacted job(s) must have this
 * field recomputed.
 *
 * Failures here are non-fatal for the calendar write (the event still
 * persists), but the driver may temporarily lose / retain job access until
 * a future write triggers a recompute. Logged loudly.
 *
 * @param {string|null|undefined} jobId
 * @returns {Promise<void>}
 */
const _recalcJobAssignedDrivers = async (jobId) => {
  if (!jobId) return;
  try {
    const calendarsRef = getCalendarCollection();
    const jobQuery = query(calendarsRef, where("jobId", "==", jobId));
    const snapshot = await getDocs(jobQuery);
    const ids = new Set();
    snapshot.forEach((d) => {
      const data = d.data();
      if (data.driverId) ids.add(data.driverId);
    });

    const jobRef = doc(db, "jobs", jobId);
    await updateDoc(jobRef, { assignedDriverIds: Array.from(ids) });
  } catch (error) {
    // Don't let job-side recompute failure cascade — the calendar write
    // itself already succeeded. Log so the user can re-trigger via a
    // subsequent save if the rule check starts misbehaving.
    console.warn(
      `Recompute of jobs/${jobId}.assignedDriverIds failed; driver rule may be stale until next write.`,
      error
    );
  }
};

// ==========================================
// MAIN CRUD OPERATIONS
// ==========================================

/**
 * Get all calendar events from the database
 *
 * Retrieves all calendar events with optional sorting by start date.
 * Includes comprehensive error handling and logging for debugging.
 *
 * @param {Object} options - Query options for filtering and sorting
 * @param {string} options.orderBy - Field to order by (default: 'start')
 * @param {string} options.orderDirection - 'asc' or 'desc' (default: 'asc')
 * @param {string} options.eventType - Filter by event type
 * @param {string} options.customerId - Filter by customer ID
 * @returns {Promise<Array>} Array of calendar event objects with IDs
 * @throws {Error} When database operation fails
 */
const getCalendars = async (options = {}) => {
  try {
    // Get collection reference
    const calendarsRef = getCalendarCollection();

    // Build query with optional filters
    let calendarQuery = calendarsRef;

    // Add filters if provided
    if (options.eventType) {
      calendarQuery = query(
        calendarQuery,
        where("eventType", "==", options.eventType)
      );
    }

    if (options.customerId) {
      calendarQuery = query(
        calendarQuery,
        where("customerId", "==", options.customerId)
      );
    }

    // Add ordering
    const orderField = options.orderBy || "start";
    const orderDirection = options.orderDirection || "asc";
    calendarQuery = query(calendarQuery, orderBy(orderField, orderDirection));

    // Execute query
    const querySnapshot = await getDocs(calendarQuery);

    // Process results
    const calendars = [];
    querySnapshot.forEach((doc) => {
      calendars.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return calendars;
  } catch (error) {
    console.error("❌ Error fetching calendar events:", error);
    throw new Error(`Failed to fetch calendar events: ${error.message}`);
  }
};

/**
 * Get a specific calendar event by ID
 *
 * Retrieves a single calendar event with comprehensive error handling.
 * Returns null if the event doesn't exist (not an error condition).
 *
 * @param {string} id - Document ID of the calendar event
 * @returns {Promise<Object|null>} Calendar event object with ID, or null if not found
 * @throws {Error} When ID is invalid or database operation fails
 */
const getCalendarById = async (id) => {
  try {
    // Validate input
    if (!id || typeof id !== "string") {
      throw new Error("Calendar ID is required and must be a string");
    }

    // Get document reference and fetch data
    const calendarRef = doc(db, CALENDAR_COLLECTION, id);
    const docSnap = await getDoc(calendarRef);

    // Check if document exists
    if (docSnap.exists()) {
      const data = docSnap.data();
      const result = { id: docSnap.id, ...data };

      return result;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`❌ Error fetching calendar event ${id}:`, error);
    throw new Error(`Failed to fetch calendar event: ${error.message}`);
  }
};

/**
 * Create a new calendar event
 *
 * Creates a new calendar event with validation, sanitization, and error handling.
 * Returns the created event with its new ID for immediate use.
 *
 * @param {Object} calendar - Calendar event data
 * @param {string} calendar.title - Event title (required)
 * @param {string} calendar.start - Start date/time (required)
 * @param {string} calendar.end - End date/time (required)
 * @param {string} calendar.description - Event description (optional)
 * @param {string} calendar.eventType - Type of event (optional)
 * @param {string} calendar.jobId - Related job ID (optional)
 * @param {string} calendar.customerId - Related customer ID (optional)
 * @returns {Promise<Object>} Created calendar event with ID
 * @throws {Error} When validation fails or database operation fails
 */
const createCalendar = async (calendar) => {
  try {
    // Validate input data
    const validation = validateCalendarData(calendar, false);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    // Denormalise customer name + phone onto the event so the driver portal
    // can render them without needing read access to the customer collection.
    const customerSlice = await _denormaliseCustomerFields(calendar);
    const calendarWithCustomer = { ...calendar, ...customerSlice };

    // Sanitize data before saving
    const sanitizedData = sanitizeCalendarData(calendarWithCustomer);

    // Add creation timestamp
    sanitizedData.createdAt = Timestamp.now();

    // Get collection reference and create document
    const calendarsRef = getCalendarCollection();
    const addedCalendar = await addDoc(calendarsRef, sanitizedData);

    // If the new event has both a jobId and a driverId, refresh the job's
    // assignedDriverIds so Firestore Rules can grant the driver read+rooms
    // access. Skip when either is missing — the recompute is a no-op
    // anyway, but skipping saves an unnecessary read.
    if (calendarWithCustomer.jobId && calendarWithCustomer.driverId) {
      await _recalcJobAssignedDrivers(calendarWithCustomer.jobId);
    }

    // Prepare return object with ID
    const result = {
      id: addedCalendar.id,
      ...sanitizedData,
    };

    return result;
  } catch (error) {
    console.error("❌ Error creating calendar event:", error);
    throw new Error(`Failed to create calendar event: ${error.message}`);
  }
};

/**
 * Update an existing calendar event
 *
 * Updates a calendar event with validation and error handling.
 * Only updates provided fields, leaving others unchanged.
 *
 * @param {string} id - Document ID of the calendar event to update
 * @param {Object} calendar - Updated calendar event data (partial updates allowed)
 * @returns {Promise<Object>} Updated calendar event data
 * @throws {Error} When ID is invalid, validation fails, or database operation fails
 */
const updateCalendar = async (id, calendar) => {
  try {
    // Validate input
    if (!id || typeof id !== "string") {
      throw new Error("Calendar ID is required and must be a string");
    }

    // Validate updated data (allow partial updates)
    const validation = validateCalendarData(calendar, true);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.error}`);
    }

    // Read existing event BEFORE writing so we can detect customerId /
    // driverId / jobId changes and refresh the denormalised state on the
    // right job(s). Caller may pass partial data, so missing keys = unchanged.
    const docRef = doc(db, CALENDAR_COLLECTION, id);
    const existingSnap = await getDoc(docRef);
    const existing = existingSnap.exists() ? existingSnap.data() : {};

    // If the customerId changed (or is being newly set), re-denormalise.
    // If the caller is only changing unrelated fields, skip the lookup.
    let payload = { ...calendar };
    const customerChanged =
      "customerId" in calendar && calendar.customerId !== existing.customerId;
    if (customerChanged) {
      const customerSlice = await _denormaliseCustomerFields(calendar);
      payload = { ...payload, ...customerSlice };
    }

    // Sanitize data before saving
    const sanitizedData = sanitizeCalendarData(payload);

    // Write the event
    await updateDoc(docRef, sanitizedData);

    // Recompute assignedDriverIds on the impacted job(s). We need to cover:
    //  - the job this event currently belongs to (post-update)
    //  - the previous job, if jobId itself just changed (so the old job
    //    correctly drops this driver if no other events on it carry it)
    const newJobId =
      "jobId" in calendar ? calendar.jobId : existing.jobId;
    const oldJobId = existing.jobId;
    const driverTouched =
      "driverId" in calendar && calendar.driverId !== existing.driverId;
    const jobTouched = newJobId !== oldJobId;

    if (driverTouched || jobTouched) {
      // Always recompute the new/current job
      if (newJobId) await _recalcJobAssignedDrivers(newJobId);
      // If jobId moved, also recompute the previous one
      if (jobTouched && oldJobId && oldJobId !== newJobId) {
        await _recalcJobAssignedDrivers(oldJobId);
      }
    }

    // Return the updated data with ID
    return {
      id,
      ...sanitizedData,
    };
  } catch (error) {
    console.error(`❌ Error updating calendar event ${id}:`, error);
    throw new Error(`Failed to update calendar event: ${error.message}`);
  }
};

/**
 * Delete a calendar event
 *
 * Permanently removes a calendar event from the database.
 * Includes safety checks and confirmation logging.
 *
 * @param {string} id - Document ID of the calendar event to delete
 * @returns {Promise<boolean>} True if deletion was successful
 * @throws {Error} When ID is invalid or database operation fails
 */
const deleteCalendar = async (id) => {
  try {
    // Validate input
    if (!id || typeof id !== "string") {
      throw new Error("Calendar ID is required and must be a string");
    }

    // Check if document exists before deletion. Also captures the jobId
    // (if any) so we can refresh the job's assignedDriverIds after the
    // event is gone.
    const calendarRef = doc(db, CALENDAR_COLLECTION, id);
    const docSnap = await getDoc(calendarRef);

    if (!docSnap.exists()) {
      return false;
    }

    const before = docSnap.data();

    // Perform deletion
    await deleteDoc(calendarRef);

    // If the deleted event was attached to a job AND had a driver, the
    // job's assignedDriverIds may need to drop that driver. Recompute
    // covers both "still has another event for the same driver" and "no
    // other events" cases.
    if (before.jobId && before.driverId) {
      await _recalcJobAssignedDrivers(before.jobId);
    }

    return true;
  } catch (error) {
    console.error(`❌ Error deleting calendar event ${id}:`, error);
    throw new Error(`Failed to delete calendar event: ${error.message}`);
  }
};

// ==========================================
// ADVANCED QUERY FUNCTIONS
// ==========================================

/**
 * Get calendar events assigned to a specific driver.
 *
 * Used by both the admin's DriverQueue page (to render the driver's queue
 * with drag-reorder) and the driver portal's "My Jobs" / "Completed" tabs.
 *
 * Sorts in JavaScript by `driverSortOrder` (ascending) with `start` as the
 * tie-breaker — avoids requiring a composite Firestore index.
 *
 * Optional `pendingOnly` filter strips out events with `status === 'done'`.
 *
 * @param {string}  driverId             Firebase Auth UID of the driver
 * @param {Object}  [opts]
 * @param {boolean} [opts.pendingOnly=false]
 * @returns {Promise<Array>}
 */
const getCalendarsByDriver = async (driverId, { pendingOnly = false } = {}) => {
  try {
    if (!driverId) {
      throw new Error("Driver ID is required");
    }
    const ref = getCalendarCollection();
    const driverQuery = query(ref, where("driverId", "==", driverId));
    const snapshot = await getDocs(driverQuery);
    const events = [];
    snapshot.forEach((d) => {
      const data = d.data();
      if (pendingOnly && data.status === "done") return;
      events.push({ id: d.id, ...data });
    });
    events.sort((a, b) => {
      const ao =
        typeof a.driverSortOrder === "number"
          ? a.driverSortOrder
          : Number.MAX_SAFE_INTEGER;
      const bo =
        typeof b.driverSortOrder === "number"
          ? b.driverSortOrder
          : Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      const da = new Date(a.start);
      const db_ = new Date(b.start);
      return da - db_;
    });
    return events;
  } catch (error) {
    console.error(`❌ Error fetching calendars for driver ${driverId}:`, error);
    throw new Error(`Failed to fetch driver calendar events: ${error.message}`);
  }
};

/**
 * Get calendar events for a specific customer
 *
 * Retrieves all calendar events associated with a specific customer,
 * ordered by start date for chronological viewing.
 *
 * @param {string} customerId - Customer ID to filter by
 * @returns {Promise<Array>} Array of calendar events for the customer
 * @throws {Error} When customerId is invalid or database operation fails
 */
const getCalendarsByCustomer = async (customerId) => {
  try {
    if (!customerId) {
      throw new Error("Customer ID is required");
    }

    return await getCalendars({
      customerId,
      orderBy: "start",
      orderDirection: "asc",
    });
  } catch (error) {
    console.error(
      `❌ Error fetching calendars for customer ${customerId}:`,
      error
    );
    throw new Error(
      `Failed to fetch customer calendar events: ${error.message}`
    );
  }
};

/**
 * Get calendar events for a specific job
 *
 * Retrieves all calendar events associated with a specific job.
 * Useful for finding measurement, installation, and follow-up appointments.
 *
 * @param {string} jobId - Job ID to filter by
 * @returns {Promise<Array>} Array of calendar events for the job
 * @throws {Error} When jobId is invalid or database operation fails
 */
const getCalendarsByJob = async (jobId) => {
  try {
    if (!jobId) {
      throw new Error("Job ID is required");
    }

    const calendarsRef = getCalendarCollection();
    // Removed orderBy to avoid Firebase index requirement
    // Will sort in JavaScript after fetching
    const jobQuery = query(
      calendarsRef,
      where("jobId", "==", jobId)
    );

    const querySnapshot = await getDocs(jobQuery);
    const calendars = [];

    querySnapshot.forEach((doc) => {
      calendars.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort by start date in JavaScript (ascending order)
    calendars.sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return dateA - dateB;
    });

    return calendars;
  } catch (error) {
    console.error(`❌ Error fetching calendars for job ${jobId}:`, error);
    throw new Error(`Failed to fetch job calendar events: ${error.message}`);
  }
};

/**
 * Get upcoming calendar events
 *
 * Retrieves calendar events scheduled for the future,
 * useful for dashboard views and notifications.
 *
 * @param {number} daysAhead - Number of days ahead to look (default: 30)
 * @returns {Promise<Array>} Array of upcoming calendar events
 * @throws {Error} When database operation fails
 */
const getUpcomingCalendars = async (daysAhead = 30) => {
  try {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + daysAhead);

    const calendarsRef = getCalendarCollection();
    const upcomingQuery = query(
      calendarsRef,
      where("start", ">=", now.toISOString()),
      where("start", "<=", futureDate.toISOString()),
      orderBy("start", "asc")
    );

    const querySnapshot = await getDocs(upcomingQuery);
    const calendars = [];

    querySnapshot.forEach((doc) => {
      calendars.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return calendars;
  } catch (error) {
    console.error("❌ Error fetching upcoming calendar events:", error);
    throw new Error(
      `Failed to fetch upcoming calendar events: ${error.message}`
    );
  }
};

// ==========================================
// EXPORTS - Public API
// ==========================================

export {
  // Main CRUD operations
  getCalendars,
  createCalendar,
  updateCalendar,
  deleteCalendar,
  getCalendarById,

  // Advanced query functions
  getCalendarsByCustomer,
  getCalendarsByJob,
  getCalendarsByDriver,
  getUpcomingCalendars,

  // Utility functions (for advanced usage)
  validateCalendarData,
  sanitizeCalendarData,
};
